class PlatefulSaleMessage extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener('variant:update', this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  handleVariantUpdate = (event) => {
    const replacement = event.detail?.data?.html?.querySelector(
      `plateful-sale-message[data-block-id="${this.dataset.blockId}"]`,
    );
    if (!replacement) return;
    this.innerHTML = replacement.innerHTML;
    this.toggleAttribute('hidden', replacement.hasAttribute('hidden'));
  };
}

if (!customElements.get('plateful-sale-message')) {
  customElements.define('plateful-sale-message', PlatefulSaleMessage);
}

class PlatefulProductBundle extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener('variant:update', this.handleVariantUpdate, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  handleVariantUpdate = (event) => {
    const replacement = event.detail?.data?.html?.querySelector(
      `plateful-product-bundle[data-block-id="${this.dataset.blockId}"]`,
    );
    if (!replacement) return;
    this.innerHTML = replacement.innerHTML;
  };
}

if (!customElements.get('plateful-product-bundle')) {
  customElements.define('plateful-product-bundle', PlatefulProductBundle);
}

class PlatefulSingleOpenAccordion extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.rows = [...this.querySelectorAll('details')];

    let openRowFound = false;
    this.rows.forEach((row) => {
      if (row.open && openRowFound) row.open = false;
      if (row.open) openRowFound = true;
      this.syncRow(row);
      row.addEventListener('toggle', () => this.handleToggle(row), { signal: this.abortController.signal });
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  handleToggle(activeRow) {
    if (activeRow.open) {
      this.rows.forEach((row) => {
        if (row !== activeRow && row.open) row.open = false;
      });
    }
    this.rows.forEach((row) => this.syncRow(row));
  }

  syncRow(row) {
    row.querySelector('summary')?.setAttribute('aria-expanded', String(row.open));
  }
}

if (!customElements.get('plateful-single-open-accordion')) {
  customElements.define('plateful-single-open-accordion', PlatefulSingleOpenAccordion);
}

class PlatefulComparison extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.viewport = this.querySelector('[data-comparison-viewport]');
    this.prevButton = this.querySelector('[data-comparison-prev]');
    this.nextButton = this.querySelector('[data-comparison-next]');
    if (!this.viewport || !this.prevButton || !this.nextButton) return;

    const options = { signal: this.abortController.signal };
    this.prevButton.addEventListener('click', () => this.scrollBy(-200), options);
    this.nextButton.addEventListener('click', () => this.scrollBy(200), options);
    this.viewport.addEventListener('scroll', this.updateControls, { ...options, passive: true });
    this.resizeObserver = new ResizeObserver(this.updateControls);
    this.resizeObserver.observe(this.viewport);
    requestAnimationFrame(this.updateControls);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.abortController = null;
  }

  scrollBy(amount) {
    this.viewport?.scrollBy({ left: amount, behavior: 'smooth' });
  }

  updateControls = () => {
    if (!this.viewport || !this.prevButton || !this.nextButton) return;
    const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const position = Math.abs(this.viewport.scrollLeft);
    this.prevButton.disabled = position <= 1;
    this.nextButton.disabled = position >= maximum - 1;
    this.prevButton.setAttribute('aria-disabled', String(this.prevButton.disabled));
    this.nextButton.setAttribute('aria-disabled', String(this.nextButton.disabled));
  };
}

if (!customElements.get('plateful-comparison')) {
  customElements.define('plateful-comparison', PlatefulComparison);
}

class PlatefulUgcCarousel extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.track = this.querySelector('[data-ugc-track]');
    this.prevButton = this.querySelector('[data-ugc-prev]');
    this.nextButton = this.querySelector('[data-ugc-next]');
    if (!this.track || !this.prevButton || !this.nextButton) return;
    const options = { signal: this.abortController.signal };
    this.prevButton.addEventListener('click', () => this.move(-1), options);
    this.nextButton.addEventListener('click', () => this.move(1), options);
    this.track.addEventListener('scroll', this.updateControls, { ...options, passive: true });
    this.resizeObserver = new ResizeObserver(this.updateControls);
    this.resizeObserver.observe(this.track);
    requestAnimationFrame(this.updateControls);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.abortController = null;
  }

  move(direction) {
    const card = this.track?.querySelector('.pf-pdp-ugc__card');
    if (!card || !this.track) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
  }

  updateControls = () => {
    if (!this.track || !this.prevButton || !this.nextButton) return;
    const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const position = Math.abs(this.track.scrollLeft);
    this.prevButton.disabled = position <= 1;
    this.nextButton.disabled = position >= maximum - 1;
    this.prevButton.setAttribute('aria-disabled', String(this.prevButton.disabled));
    this.nextButton.setAttribute('aria-disabled', String(this.nextButton.disabled));
  };
}

if (!customElements.get('plateful-ugc-carousel')) {
  customElements.define('plateful-ugc-carousel', PlatefulUgcCarousel);
}

class PlatefulPdpReviews extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.cards = [...this.querySelectorAll('[data-review-card]')];
    this.button = this.querySelector('[data-reviews-more]');
    this.initialCount = Math.max(1, Number.parseInt(this.dataset.initialCount, 10) || 5);
    this.loadCount = Math.max(1, Number.parseInt(this.dataset.loadCount, 10) || 2);
    this.visibleCount = Math.min(this.initialCount, this.cards.length);
    this.cards.forEach((card, index) => this.setCardVisibility(card, index < this.visibleCount));
    this.button?.addEventListener('click', this.loadMore, { signal: this.abortController.signal });
    this.updateButton();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  loadMore = () => {
    const nextCount = Math.min(this.cards.length, this.visibleCount + this.loadCount);
    this.cards.slice(this.visibleCount, nextCount).forEach((card) => this.setCardVisibility(card, true));
    this.visibleCount = nextCount;
    this.updateButton();
  };

  updateButton() {
    if (!this.button) return;
    const complete = this.visibleCount >= this.cards.length;
    this.button.disabled = complete;
    this.button.hidden = complete;
    this.button.setAttribute('aria-disabled', String(complete));
    this.button.setAttribute('aria-hidden', String(complete));
  }

  setCardVisibility(card, visible) {
    card.hidden = !visible;
    card.classList.toggle('is-hidden', !visible);
    card.setAttribute('aria-hidden', String(!visible));
  }
}

if (!customElements.get('plateful-pdp-reviews')) {
  customElements.define('plateful-pdp-reviews', PlatefulPdpReviews);
}

class PlatefulSingleOpenPersonas extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    this.rows = [...this.querySelectorAll(':scope > details')];
    this.animations = new Map();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileViewport = window.matchMedia('(max-width: 767px)');

    this.rows.forEach((row, index) => {
      row.open = index === 0;
      this.syncRow(row);
      const panel = row.querySelector('.pf-pdp-persona__content');
      if (panel) panel.style.height = index === 0 ? 'auto' : '0px';
      row.querySelector('summary')?.addEventListener('click', (event) => this.handleClick(event, row), {
        signal: this.abortController.signal,
      });
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  handleClick(event, activeRow) {
    event.preventDefault();
    const isClosing = activeRow.open && this.animations.get(activeRow)?.direction !== 'closing';

    if (isClosing) {
      this.animateRow(activeRow, false);
      return;
    }

    this.rows.forEach((row) => {
      if (row !== activeRow && row.open) this.animateRow(row, false);
    });
    this.animateRow(activeRow, true);
  }

  animateRow(row, opening) {
    const panel = row.querySelector('.pf-pdp-persona__content');
    if (!panel) return;

    const running = this.animations.get(row);
    const wasOpen = row.open;
    const currentHeight = panel.getBoundingClientRect().height;
    const currentStyles = getComputedStyle(panel);
    const currentOpacity = opening && !wasOpen ? 0 : Number.parseFloat(currentStyles.opacity) || 0;
    const currentTransform = opening && !wasOpen
      ? 'translateY(-4px)'
      : currentStyles.transform === 'none'
        ? 'translateY(0)'
        : currentStyles.transform;
    running?.heightAnimation.cancel();
    running?.contentAnimation.cancel();

    if (this.reducedMotion.matches) {
      row.open = opening;
      panel.style.height = opening ? 'auto' : '0px';
      panel.style.overflow = opening ? 'visible' : 'hidden';
      this.animations.delete(row);
      this.syncRow(row);
      return;
    }

    if (opening) row.open = true;
    panel.style.height = 'auto';
    const targetHeight = opening ? panel.scrollHeight : 0;
    panel.style.height = `${currentHeight}px`;
    panel.style.overflow = 'hidden';
    this.syncRow(row, opening);

    const heightAnimation = panel.animate(
      [{ height: `${currentHeight}px` }, { height: `${targetHeight}px` }],
      {
        duration: this.mobileViewport.matches ? 380 : 440,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      },
    );
    const contentAnimation = panel.animate(
      opening
        ? [{ opacity: currentOpacity, transform: currentTransform }, { opacity: 1, transform: 'translateY(0)' }]
        : [{ opacity: currentOpacity, transform: currentTransform }, { opacity: 0, transform: 'translateY(-3px)' }],
      { duration: this.mobileViewport.matches ? 240 : 260, easing: 'ease-out', fill: 'forwards' },
    );
    const state = { direction: opening ? 'opening' : 'closing', heightAnimation, contentAnimation };
    this.animations.set(row, state);

    heightAnimation.finished
      .then(() => {
        if (this.animations.get(row) !== state) return;
        heightAnimation.cancel();
        contentAnimation.cancel();
        row.open = opening;
        panel.style.height = opening ? 'auto' : '0px';
        panel.style.overflow = opening ? 'visible' : 'hidden';
        panel.style.opacity = '';
        panel.style.transform = '';
        this.animations.delete(row);
        this.syncRow(row);
      })
      .catch(() => {});
  }

  syncRow(row, expanded = row.open) {
    const summary = row.querySelector('summary');
    summary?.setAttribute('aria-expanded', String(expanded));
    row.classList.toggle('is-expanded', expanded);
  }
}

if (!customElements.get('plateful-single-open-personas')) {
  customElements.define('plateful-single-open-personas', PlatefulSingleOpenPersonas);
}
